import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, Plus, X, Copy, CheckCircle2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * HookLibrary — searchable library of proven hooks, captions, scripts.
 *
 * Backend already provides /api/media/hooks (CRUD + approval flag).
 * UI offers: text filter, type/category filter, approved-only toggle,
 * one-click copy of any hook body, admin approval toggle, and a quick
 * "Add hook" form.
 */
const HookLibrary = ({ auth, options, isAdmin, onChange }) => {
  const [hooks, setHooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState({ hook_type: '', category: '', approved_only: false });
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (filters.hook_type) qs.append('hook_type', filters.hook_type);
    if (filters.category) qs.append('category', filters.category);
    if (filters.approved_only) qs.append('approved_only', 'true');
    const r = await fetch(`${API}/api/media/hooks?${qs}`, { headers: auth });
    setHooks(await r.json());
    setLoading(false);
  }, [auth, filters]);
  useEffect(() => { load(); }, [load]);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? hooks.filter(h =>
        (h.title || '').toLowerCase().includes(q) ||
        (h.body || '').toLowerCase().includes(q) ||
        (h.best_used_for || '').toLowerCase().includes(q))
    : hooks;

  const toggleApproved = async (h) => {
    const r = await fetch(`${API}/api/media/hooks/${h.id}`, {
      method: 'PATCH', headers: { ...auth, 'Content-Type': 'application/json' },
      body: JSON.stringify({ approved_for_use: !h.approved_for_use }),
    });
    if (r.ok) { toast.success(h.approved_for_use ? 'Marked unapproved' : 'Approved for use'); load(); onChange?.(); }
    else toast.error('Only admins can approve hooks');
  };

  const copy = async (h) => {
    try { await navigator.clipboard.writeText(h.body || ''); toast.success('Copied'); }
    catch (e) { toast.error('Clipboard blocked'); }
  };

  const remove = async (h) => {
    if (!window.confirm(`Delete "${h.title}"?`)) return;
    const r = await fetch(`${API}/api/media/hooks/${h.id}`, { method: 'DELETE', headers: auth });
    if (r.ok) { toast.success('Deleted'); load(); onChange?.(); }
    else toast.error('Only admins can delete');
  };

  return (
    <div className="space-y-4" data-testid="hook-library">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="flex items-center gap-2 bg-surface border border-surface-border rounded-xl px-3 py-1.5 flex-1 sm:max-w-sm">
          <Search className="w-4 h-4 text-text-muted shrink-0" />
          <input
            value={query} onChange={e => setQuery(e.target.value)}
            placeholder="Search hooks, scripts…"
            className="flex-1 bg-transparent outline-none text-sm placeholder:text-text-muted"
            data-testid="hook-search"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={filters.hook_type || 'all'} onValueChange={v => setFilters(f => ({ ...f, hook_type: v === 'all' ? '' : v }))}>
            <SelectTrigger className="w-36 h-9 text-xs"><SelectValue placeholder="All types" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {options?.hook_types?.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.category || 'all'} onValueChange={v => setFilters(f => ({ ...f, category: v === 'all' ? '' : v }))}>
            <SelectTrigger className="w-40 h-9 text-xs"><SelectValue placeholder="All categories" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {options?.categories?.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <button
            type="button"
            onClick={() => setFilters(f => ({ ...f, approved_only: !f.approved_only }))}
            className={`px-3 h-9 rounded-md text-xs font-semibold border ${filters.approved_only ? 'bg-emerald-500/10 text-emerald-600 border-emerald-300' : 'bg-surface text-text-muted border-surface-border'}`}
            data-testid="hook-approved-toggle"
          >
            Approved only
          </button>
        </div>
        <Button onClick={() => setCreating(true)} className="bg-crm-blue hover:bg-crm-blue-light text-white" data-testid="new-hook-btn">
          <Plus className="w-4 h-4 mr-1" /> Add hook
        </Button>
      </div>

      {creating && (
        <HookForm auth={auth} options={options} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); onChange?.(); }} />
      )}

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-text-muted" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-text-muted">
          {query ? 'No hooks match your search.' : 'No hooks yet — add the first proven angle.'}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map(h => (
            <div key={h.id} className="bg-surface border border-surface-border rounded-xl p-4 shadow-sm flex flex-col gap-2" data-testid={`hook-${h.id}`}>
              <div className="flex items-start justify-between gap-2">
                <h3 className="text-sm font-bold text-text-main leading-tight">{h.title}</h3>
                {h.approved_for_use && (
                  <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 whitespace-nowrap inline-flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Approved
                  </span>
                )}
              </div>
              <p className="text-xs text-text-main whitespace-pre-wrap line-clamp-5">{h.body}</p>
              <div className="flex flex-wrap gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-text-muted">
                <span className="px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800">{h.hook_type}</span>
                <span className="px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800">{h.category}</span>
                {h.best_used_for && <span className="px-2 py-0.5 rounded-full bg-crm-teal/10 text-crm-teal normal-case tracking-normal font-semibold">Best for: {h.best_used_for}</span>}
              </div>
              {h.performance_notes && (
                <p className="text-[11px] text-text-muted italic">"{h.performance_notes}"</p>
              )}
              <div className="flex items-center justify-between pt-1 border-t border-surface-border mt-1 text-[11px] text-text-muted">
                <span>{h.added_by_name || '—'}</span>
                <div className="flex items-center gap-1">
                  <button onClick={() => copy(h)} className="px-2 py-0.5 hover:bg-crm-blue/10 hover:text-crm-blue rounded" title="Copy body">
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                  {isAdmin && (
                    <>
                      <button onClick={() => toggleApproved(h)} className={`px-2 py-0.5 rounded ${h.approved_for_use ? 'text-text-muted hover:bg-slate-100' : 'text-emerald-600 hover:bg-emerald-500/10'}`} title={h.approved_for_use ? 'Unapprove' : 'Approve'}>
                        <CheckCircle2 className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => remove(h)} className="px-2 py-0.5 text-crm-red hover:bg-crm-red/10 rounded" title="Delete">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const HookForm = ({ auth, options, onClose, onSaved }) => {
  const [f, setF] = useState({ title: '', hook_type: '', category: '', body: '', best_used_for: '', performance_notes: '', approved_for_use: false });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.title.trim() || !f.hook_type || !f.category || !f.body.trim()) {
      toast.error('Title, type, category and body are required'); return;
    }
    setSaving(true);
    const r = await fetch(`${API}/api/media/hooks`, {
      method: 'POST', headers: { ...auth, 'Content-Type': 'application/json' }, body: JSON.stringify(f),
    });
    setSaving(false);
    if (r.ok) { toast.success('Hook added'); onSaved(); }
    else toast.error('Failed to save');
  };

  return (
    <form onSubmit={submit} className="bg-surface border border-surface-border rounded-2xl p-4 shadow-soft space-y-3" data-testid="hook-form">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-bold text-text-main">Add a hook / script</h3>
        <button type="button" onClick={onClose} aria-label="Close"><X className="w-4 h-4 text-text-muted" /></button>
      </div>
      <Input placeholder='Hook title — e.g. "Your bank does not decide your future"' value={f.title} onChange={e => setF({ ...f, title: e.target.value })} data-testid="hook-title" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Select value={f.hook_type} onValueChange={v => setF({ ...f, hook_type: v })}>
          <SelectTrigger data-testid="hook-type"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>{options?.hook_types?.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={f.category} onValueChange={v => setF({ ...f, category: v })}>
          <SelectTrigger data-testid="hook-category"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>{options?.categories?.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <Textarea placeholder="Hook / script / caption body" value={f.body} onChange={e => setF({ ...f, body: e.target.value })} rows={5} data-testid="hook-body" />
      <Input placeholder="Best used for — e.g. Instagram Reel, TikTok, Facebook ad" value={f.best_used_for} onChange={e => setF({ ...f, best_used_for: e.target.value })} />
      <Textarea placeholder="Performance notes (optional)" value={f.performance_notes} onChange={e => setF({ ...f, performance_notes: e.target.value })} rows={2} />
      <div className="flex items-center justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={saving} className="bg-crm-blue hover:bg-crm-blue-light text-white" data-testid="hook-submit">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save hook'}
        </Button>
      </div>
    </form>
  );
};

export default HookLibrary;
